import {
	type AutocompleteFetcher,
	type AutocompleteResult,
	createEmptyAutocompleteResult,
} from './types'

const normalizeQuery = (query: string): string =>
	query.trim().replace(/\s+/g, ' ')

const isEmptyResult = (result: AutocompleteResult): boolean =>
	result.suggestionHits.length === 0 &&
	result.facetHits.length === 0 &&
	result.playlistHits.length === 0 &&
	result.collectionHits.length === 0

/**
 * Wraps an autocomplete fetcher to skip work when the normalized query matches
 * the last cached one, or when the user keeps extending a query that already
 * returned zero hits past the configured cap. Resets when the user broadens
 * the query.
 */
export const createGuardedAutocompleteFetch = (
	inner: AutocompleteFetcher,
	maxConsecutiveZeroResultRefinements: number
): AutocompleteFetcher => {
	let lastNormalizedQuery: string | null = null
	let lastResult: AutocompleteResult = createEmptyAutocompleteResult()
	let lastZeroResultQuery: string | null = null
	let consecutiveZeroFetches = 0

	return async (query: string): Promise<AutocompleteResult> => {
		const normalized = normalizeQuery(query)

		if (normalized === lastNormalizedQuery) {
			return lastResult
		}

		const isSupersetOfDeadQuery =
			lastZeroResultQuery !== null && normalized.startsWith(lastZeroResultQuery)

		if (
			isSupersetOfDeadQuery &&
			consecutiveZeroFetches >= maxConsecutiveZeroResultRefinements
		) {
			return lastResult
		}

		if (!isSupersetOfDeadQuery) {
			lastZeroResultQuery = null
			consecutiveZeroFetches = 0
		}

		const result = await inner(query)

		lastNormalizedQuery = normalized
		lastResult = result
		if (isEmptyResult(result)) {
			lastZeroResultQuery = normalized
			consecutiveZeroFetches += 1
		} else {
			lastZeroResultQuery = null
			consecutiveZeroFetches = 0
		}

		return result
	}
}
